import { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { Stack, Box, Dialog, DialogContent, Typography, TextField, Tooltip, Checkbox } from '@mui/material';
import axios from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import { UserContext } from '../../context/UserProvider';

SubmitOrderSummary.propTypes = {
  facilityAddress: PropTypes.object,
  formData: PropTypes.array,
  isAllCarts: PropTypes.bool,
  selectedFacility: PropTypes.object,
  open: PropTypes.bool,
  close: PropTypes.func
}

export default function SubmitOrderSummary({ facilityAddress, formData, isAllCarts, selectedFacility, open, close }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { isInRole } = useContext(UserContext);
  const isAdmin = isInRole("Admin");
  const [restrictCustomerInvoices, setRestrictCustomerInvoices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerPONumber, setCustomerPONumber] = useState("");

  useEffect(() => {
    setCustomerPONumber("")
    if (formData[0]?.purchaseOrderNumber) {
      setCustomerPONumber(formData[0]?.purchaseOrderNumber);
    }
  }, [formData]);

  const submitCart = () => {
    setSubmitting(true)
    axios.post(`/cart/submitOrder`, objectToFormData({ items: formData, facilityId: selectedFacility?.value, restrictCustomerInvoices, isAllCarts }))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar(isAllCarts ? 'Orders submitted' : 'Order Submitted', { variant: 'success' });
          navigate("/admin/orders");
        } else enqueueSnackbar(isAllCarts ? 'One order or more failed to submit' : 'Submit order failed', { variant: 'error' });
      })
      .catch(() => enqueueSnackbar(isAllCarts ? 'Submit orders failed' : 'Submit order failed', { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  return <Dialog open={open} onClose={() => { close(); setSubmitting(false); }} maxWidth="sm" fullWidth>
    <DialogContent>
      <Stack spacing={1}>
        <Box>
          {isAllCarts && <Typography variant="subtitle1" fontWeight="bold" align="center"> All Facility Carts </Typography>}
          <Typography variant="subtitle1" fontWeight="bold" align="center">{selectedFacility?.label}</Typography>
          <Typography variant="subtitle2">{facilityAddress?.streetAddress}</Typography>
          <Typography variant="subtitle2">{[facilityAddress?.city, facilityAddress?.state, facilityAddress?.zip].filter(x => x).join(", ")}</Typography>
          <Typography variant="subtitle2">{facilityAddress?.phoneNumber}</Typography>
        </Box>
        {!isAllCarts ? <Box>
          <Typography variant="subtitle1">Customer PO Number</Typography>
          <TextField name="customerPONumber" size="small" placeholder="Enter Customer PO Number" fullWidth value={customerPONumber}
            onChange={(e) => setCustomerPONumber(e.target.value)} />
        </Box> : (
          <Box>
            <Typography variant="subtitle1">Carts</Typography>
            {[...new Set(formData.map(item => item.purchaseOrderNumber))].map((pon, index) => (
              <Typography key={index} variant="subtitle2">
                {pon}
              </Typography>
            ))}
          </Box>
        )}
        <Typography variant="subtitle1">Total: {formData.reduce((acc, prod) => acc + prod.quantity * prod.price, 0)
          .toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</Typography>
        {isAdmin && <Box sx={{ display: "flex", alignItems: "end" }}>
          <Tooltip arrow title=" Restricts electronic invoices from sending out to the customer. Only manual invoices can be sent for this order.">
            <Typography>
              <Checkbox disableRipple sx={{ pl: 0 }} checked={restrictCustomerInvoices} onChange={(e) => setRestrictCustomerInvoices(e.target.checked)} />
              Restrict Customer Invoices
            </Typography>
          </Tooltip>
        </Box>}
        <Box>
          <LoadingButton loading={submitting} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }}
            color="purple" size="medium" variant="contained" onClick={submitCart}>
            {isAllCarts ? "Submit Carts" : "Submit Cart"}
          </LoadingButton>
        </Box>
      </Stack>
    </DialogContent>
  </Dialog>;
}
